import { ref } from 'vue'
import { sendGraphQLQuery } from '@/services/gql-util'

export interface GameReward {
  id: number
  name: string
  icon: string
  pointvalue: number
  coupon: number
  asset: string | null
}

export interface GameRule {
  id: number
  label: string
  action: string
  rewardid: number
  maxuses: number
  type: number
  destchannel: number
  destgroup: number
  destcollection: number
  parentid: number
  notificationid: number
  notifychildren: number
  emailnotification: string
  logtext: string
  milestonelogic: string
  ruleOrder: number
  field: string
  settings: Record<string, any>
}

export interface GameMilestone {
  id: number
  label: string
  actionid: number
  threshold: number
  rewardid: number
  grouping: string
  notificationid: number
  emailnotification: number
  advanced: Record<string, any>
  rewardInfo?: GameReward
  ruleInfo?: GameRule
}

export interface GameType {
  id: number
  type: string
}

export interface MilestoneGroup {
  grouping: string
}

export default function useGamification() {

  const loading = ref(false)

  // ─── Rewards ────────────────────────────────────────────────
  const fetchRewards = async (vhost: number): Promise<GameReward[]> => {
    const data = await sendGraphQLQuery(`{
      Game {
        rewards(vhost: ${vhost}) {
          id name icon pointvalue coupon asset
        }
      }
    }`)
    return data.Game.rewards ?? []
  }

  const createReward = async (vhost: number, settings: Record<string, any>): Promise<number> => {
    const data = await sendGraphQLQuery(
      `mutation ($vhost: Int!, $settings: JSON!) {
        GameMutations {
          createReward(vhost: $vhost, settings: $settings)
        }
      }`,
      { vhost, settings }
    )
    return data.GameMutations.createReward
  }

  const deleteReward = async (vhost: number, rewardId: number): Promise<boolean> => {
    const data = await sendGraphQLQuery(
      `mutation ($vhost: Int!, $rewardId: Int!) {
        GameMutations {
          deleteReward(vhost: $vhost, rewardId: $rewardId)
        }
      }`,
      { vhost, rewardId }
    )
    return data.GameMutations.deleteReward
  }

  // ─── Rules ──────────────────────────────────────────────────
  const fetchRules = async (vhost: number): Promise<GameRule[]> => {
    const data = await sendGraphQLQuery(`{
      Game {
        rules(vhost: ${vhost}) {
          id label action rewardid maxuses type
          destchannel destgroup destcollection parentid
          notificationid notifychildren emailnotification
          logtext milestonelogic ruleOrder field settings
        }
      }
    }`)
    return data.Game.rules ?? []
  }

  const createRule = async (vhost: number, ruleData: Record<string, any>): Promise<number> => {
    const data = await sendGraphQLQuery(
      `mutation ($vhost: Int!, $data: JSON!) {
        GameMutations {
          createRule(vhost: $vhost, data: $data)
        }
      }`,
      { vhost, data: ruleData }
    )
    return data.GameMutations.createRule
  }

  const deleteRule = async (vhost: number, ruleId: number): Promise<boolean> => {
    const data = await sendGraphQLQuery(
      `mutation ($vhost: Int!, $ruleId: Int!) {
        GameMutations {
          deleteRule(vhost: $vhost, ruleId: $ruleId)
        }
      }`,
      { vhost, ruleId }
    )
    return data.GameMutations.deleteRule
  }

  // ─── Milestones ─────────────────────────────────────────────
  const fetchMilestones = async (vhost: number): Promise<GameMilestone[]> => {
    const data = await sendGraphQLQuery(`{
      Game {
        milestones(vhost: ${vhost}) {
          id label actionid threshold rewardid grouping
          notificationid emailnotification advanced
        }
      }
    }`)
    return data.Game.milestones ?? []
  }

  const createMilestone = async (vhost: number, milestoneData: Record<string, any>): Promise<number> => {
    const data = await sendGraphQLQuery(
      `mutation ($vhost: Int!, $data: JSON!) {
        GameMutations {
          createMilestone(vhost: $vhost, data: $data)
        }
      }`,
      { vhost, data: milestoneData }
    )
    return data.GameMutations.createMilestone
  }

  const updateMilestone = async (vhost: number, milestoneId: number, settings: Record<string, any>): Promise<boolean> => {
    const data = await sendGraphQLQuery(
      `mutation ($vhost: Int!, $milestoneId: Int!, $settings: JSON!) {
        GameMutations {
          updateMilestone(vhost: $vhost, milestoneId: $milestoneId, settings: $settings)
        }
      }`,
      { vhost, milestoneId, settings }
    )
    return data.GameMutations.updateMilestone
  }

  const deleteMilestone = async (vhost: number, milestoneId: number): Promise<boolean> => {
    const data = await sendGraphQLQuery(
      `mutation ($vhost: Int!, $milestoneId: Int!) {
        GameMutations {
          deleteMilestone(vhost: $vhost, milestoneId: $milestoneId)
        }
      }`,
      { vhost, milestoneId }
    )
    return data.GameMutations.deleteMilestone
  }

  return {
    loading,
    fetchRewards,
    createReward,
    deleteReward,
    fetchRules,
    createRule,
    deleteRule,
    fetchMilestones,
    createMilestone,
    updateMilestone,
    deleteMilestone,
  }
}
